import AiAllowance from './AiAllowance.jsx';
// src/components/AiStudyPartnerPanel.jsx (WP-6 Slice 2)
//
// Student-facing AI Study Partner chat panel. Design language migrated from the
// prototype AI drawer (prototype/between-the-lines-v2.html ~L14556-14620): book
// theme, sparkles avatar, chat bubbles, typing dots, textarea + send.
//
// Wired to the onAiChat Cloud Function (callables.js). The CF grounds answers in
// the pack content, gates paid material by entitlement, and enforces the Gate-2
// cost guardrails -- the UI surfaces the CF's friendly quota/error messages.
// In mock mode there is no backend, so the panel shows a notice (P7 invariant).
import React, { useState, useRef, useEffect } from 'react';
import { sendEmailVerification } from 'firebase/auth';
import { IconSparkles, IconSend, IconX } from './icons.jsx';
import ChatMarkdown from './ChatMarkdown.jsx';
import { stripDanielleReviewNotice } from '../../functions/src/ai/reviewNotice.js';
import { onAiChat } from '../lib/callables.js';
import { AUTH_MODE } from '../auth/firebase.js';
import { useAuth } from '../auth/useAuth.js';
import { useNote } from '../storage/useUserContent.js';
import { aiWaitCopy, AI_WAIT_ESCALATE_MS } from './aiWaitCopy.js';
import { useEntitlements } from '../storage/useEntitlements.js';
import { aiQuotaCopy } from './aiQuotaCopy.js';
import { loadChat, saveChat, clearChat, historyEnabled, setHistoryEnabled } from '../storage/localChatHistory.js';

const HISTORY_TURNS = 6; // mirror the backend cap
const NOTE_BODY_MAX = 5000; // firestore.rules validNote cap (body.size() <= 5000)

// `onClose` is optional: when hosted in the reader drawer it renders the close (X)
// IN the header (one compact bar instead of a separate close strip + header, so the
// message list gets more height); the standalone StudyPartnerPage omits it.
// `currentItem*` describe the reader's active section so an AI answer can be saved
// into that section's note (Slice 3) and selected for relevant AI context. The ID
// is only a hint: the server still enforces access. Standalone omits the hint.
export default function AiStudyPartnerPanel({ packId, packTitle, onClose, currentItemId, currentItemType, currentItemTitle }) {
    const { currentUser } = useAuth();
    const conversationKey = `${currentUser?.uid || ''}/${packId}`;
    const activeConversation = useRef(conversationKey);
    activeConversation.current = conversationKey;
    // Admin reader preview is not a paid AI entitlement. Match the backend gate.
    const { activePackIds } = useEntitlements();
    const entitled = activePackIds.has(packId);
    // Notes write path (reuse useNote -- one note doc per packId+itemId; no new schema).
    // `noteUid` is truthy only in firebase/emulator mode + signed-in + a current section,
    // which is exactly when "Save to Notes" should appear (notes need no entitlement).
    const { note, saveNote, uid: noteUid } = useNote(packId, currentItemId);
    const [savedIdx, setSavedIdx] = useState(null);   // index of the bubble just saved
    const [saveErrIdx, setSaveErrIdx] = useState(null);

    // Append the AI answer to the current section's note (NON-DESTRUCTIVE: the single
    // note-per-item model means we append rather than overwrite; an existing user title
    // is preserved). Bounded to the rules' 5000-char body cap.
    const saveAnswerToNotes = async (answerText, idx) => {
        if (!noteUid) return;
        setSaveErrIdx(null);
        const block = 'AI Study Partner:\n' + String(answerText || '').trim();
        const existing = (note && typeof note.body === 'string') ? note.body : '';
        let combined = existing ? existing + '\n\n----------\n\n' + block : block;
        if (combined.length > NOTE_BODY_MAX) combined = combined.slice(0, NOTE_BODY_MAX);
        // Keep the user's existing title; only mint one when creating a fresh note.
        const aiTitle = 'AI: ' + String(answerText || '').trim().split(/\s+/).slice(0, 6).join(' ');
        const title = (note && note.title) ? note.title : (aiTitle || currentItemTitle || 'Notes');
        try {
            await saveNote(combined, { itemType: currentItemType || null, title });
            setSavedIdx(idx); setTimeout(() => setSavedIdx((v) => (v === idx ? null : v)), 2000);
        } catch {
            setSaveErrIdx(idx);
        }
    };

    const greeting = {
        role: 'assistant',
        text: `Hello -- I'm your AI Study Partner for "${packTitle || 'this pack'}". `
            + 'Ask me about themes, language, structure, or how to approach an essay '
            + 'question. I work from the pack material available to your account.'
    };
    const [messages, setMessages] = useState([greeting]);
    const [historyReady, setHistoryReady] = useState(null);
    const [keepHistory, setKeepHistory] = useState(historyEnabled);
    const [historyError, setHistoryError] = useState('');
    useEffect(() => {
        let live = true;
        setHistoryReady(null); setTyping(false); setInput(''); setMessages([greeting]);
        loadChat(currentUser?.uid, packId).then(saved => {
            if (live) { if (saved.length) setMessages(saved.map((m, i) => i === 0 && m.role === 'assistant' && m.text.startsWith("Hello -- I'm your AI Study Partner for ") ? greeting : m)); setHistoryReady(conversationKey); }
        }).catch(() => { if (live) { setHistoryReady(conversationKey); setHistoryError('History is unavailable in this browser.'); } });
        return () => { live = false; };
    }, [conversationKey]);
    useEffect(() => {
        const sync = () => setKeepHistory(historyEnabled());
        window.addEventListener('btl-history-preference', sync);
        return () => window.removeEventListener('btl-history-preference', sync);
    }, []);
    useEffect(() => {
        if (historyReady === conversationKey && keepHistory) saveChat(currentUser?.uid, packId, messages).catch(() => setHistoryError('History could not be saved on this device.'));
    }, [messages, historyReady, conversationKey, keepHistory]);
    const [input, setInput] = useState('');
    const [typing, setTyping] = useState(false);
    // The ~20s AI call is non-streamed, so the wait needs to read as intentional.
    // `waitLong` flips true once a single continuous wait crosses the threshold, which
    // only escalates the reassurance copy -- it never changes the AI call or its result.
    const [waitLong, setWaitLong] = useState(false);
    const [disclaimer, setDisclaimer] = useState(
        'AI study help based on Between the Lines course materials. It can make '
        + 'mistakes and is not a substitute for your teacher.'
    );
    const endRef = useRef(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, typing]);

    // Escalate the wait copy after a continuous threshold. The cleanup clears the
    // timer whenever `typing` ends (response/error via the send() finally) or the
    // panel unmounts, and the reset keeps the copy calm for the next turn.
    useEffect(() => {
        if (!typing) { setWaitLong(false); return; }
        const t = setTimeout(() => setWaitLong(true), AI_WAIT_ESCALATE_MS);
        return () => clearTimeout(t);
    }, [typing]);

    const mockMode = AUTH_MODE === 'mock';

    // Free-tier email-verification gate (UX; onAiChat is the HARD gate). Blocks only a
    // signed-in, NON-entitled user whose email is explicitly unverified, in firebase/
    // emulator mode. Google sign-ins arrive emailVerified=true; mock mode is exempt
    // (no real verification). Entitled/verified users see the normal chat.
    const [verifySending, setVerifySending] = useState(false);
    const [verifySent, setVerifySent] = useState(false);
    const [verifyErr, setVerifyErr] = useState(null);
    const needsVerify = !mockMode && !entitled && !!currentUser && currentUser.emailVerified === false;
    const resendVerify = async () => {
        if (!currentUser || verifySending) return;
        setVerifyErr(null); setVerifySending(true);
        try { await sendEmailVerification(currentUser); setVerifySent(true); }
        catch { setVerifyErr('Could not send right now. Please try again in a moment.'); }
        finally { setVerifySending(false); }
    };

    const send = async () => {
        const text = input.trim();
        if (!text || typing || historyReady !== conversationKey) return;
        const requestConversation = conversationKey;
        const userMsg = { role: 'user', text };
        const history = messages
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .slice(-HISTORY_TURNS)
            .map((m) => ({ role: m.role, text: m.text }));
        setMessages((prev) => [...prev, userMsg]);
        setInput('');
        setTyping(true);
        try {
            const res = await onAiChat({ packId, message: text, history,
                ...(currentItemId ? { currentItemId } : {}) });
            if (activeConversation.current !== requestConversation) return;
            if (res?.disclaimer) setDisclaimer(res.disclaimer);
            setMessages((prev) => [...prev, { role: 'assistant', text: res.answer }]);
        } catch (err) {
            if (activeConversation.current !== requestConversation) return;
            // The CF sends friendly messages for quota/unavailable/invalid cases.
            const msg = aiQuotaCopy(err?.message) || 'Something went wrong. Please try again.';
            setMessages((prev) => [...prev, { role: 'error', text: msg }]);
        } finally {
            if (activeConversation.current === requestConversation) setTyping(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-book-pageDark border border-book-accent/30 shadow-[0_10px_30px_rgba(8,16,12,0.3)]">
            {/* Header (compact: tighter padding + the close X lives here when hosted in
                the drawer, so there is ONE bar and the conversation gets more height). */}
            <div className="flex-shrink-0 px-3 py-2 border-b border-book-accent/20 flex items-center gap-2 bg-book-bg">
                <div className="w-7 h-7 rounded-full bg-book-accent/10 border border-book-accent flex items-center justify-center flex-shrink-0">
                    <IconSparkles className="w-3.5 h-3.5 text-book-accent" />
                </div>
                <div className="min-w-0 flex-grow">
                    <div className="font-display text-book-accent text-xs leading-tight font-semibold">AI Study Partner</div>
                    <div className="font-serif italic text-book-muted text-xs leading-tight truncate font-semibold">
                        {packTitle}{entitled ? '' : ' · free preview'}
                    </div>
                </div>
                {onClose && (
                    <button onClick={onClose} type="button" aria-label="Close AI Study Partner"
                        className="flex-shrink-0 -mr-1 w-9 h-9 flex items-center justify-center text-book-muted hover:text-book-accent transition-colors">
                        <IconX className="w-5 h-5" />
                    </button>
                )}
            </div>

            {needsVerify ? (
                /* Free-tier email-verification gate: replaces the chat for an unverified
                   free user (premium dark/gold). The backend (onAiChat) enforces it too. */
                <div className="flex-grow overflow-y-auto custom-scrollbar p-6 flex flex-col items-center justify-center text-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-book-accent/10 border border-book-accent flex items-center justify-center">
                        <IconSparkles className="w-5 h-5 text-book-accent" />
                    </div>
                    <div className="font-display text-book-accent text-xs font-semibold">Verify your email</div>
                    <p className="font-serif text-book-text text-sm leading-relaxed max-w-xs">
                        To use the free study partner, please verify your email. We sent a link when you signed up — check your inbox (and spam).
                    </p>
                    <button type="button" onClick={resendVerify} disabled={verifySending || verifySent}
                        className="px-4 py-2 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold">
                        {verifySent ? 'Verification email sent' : verifySending ? 'Sending…' : 'Resend verification email'}
                    </button>
                    {verifyErr && <p className="font-serif text-red-300 text-xs">{verifyErr}</p>}
                    <p className="font-serif italic text-book-muted text-xs max-w-xs font-semibold">
                        After verifying, reopen this panel. Paid members skip this step.
                    </p>
                </div>
            ) : (<>
            {/* Messages */}
            <div className="flex-grow overflow-y-auto custom-scrollbar p-4 space-y-4" style={{ minHeight: 0 }}>
                {messages.map((msg, i) => (
                    <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                        {msg.role !== 'user' && (
                            <div className="w-7 h-7 rounded-full bg-book-accent/10 border border-book-accent flex items-center justify-center flex-shrink-0">
                                <IconSparkles className="w-3.5 h-3.5 text-book-accent" />
                            </div>
                        )}
                        <div className={`max-w-[85%] p-3 text-sm ${msg.role === 'assistant' ? '' : 'whitespace-pre-wrap'} ${
                            msg.role === 'user'
                                ? 'bg-book-accent text-black rounded-l-lg rounded-tr-lg'
                                : msg.role === 'error'
                                    ? 'border border-red-400/40 bg-red-900/20 text-red-200 rounded-r-lg rounded-tl-lg'
                                    : 'border border-book-accent/20 bg-book-page text-book-text rounded-r-lg rounded-tl-lg'
                        }`}>
                            {/* AI answers are markdown -> render the SAFE subset (no raw HTML).
                                User + error messages stay plain text (never markdown-parsed). */}
                            {msg.role === 'assistant'
                                ? <ChatMarkdown>{msg.text}</ChatMarkdown>
                                : <p className="font-serif leading-relaxed">{msg.text}</p>}
                            {/* Save-to-Notes: real AI answers only (not the greeting/errors),
                                and only when a current section + signed-in (noteUid). */}
                            {msg.role === 'assistant' && i > 0 && noteUid && (
                                <button type="button" onClick={() => saveAnswerToNotes(stripDanielleReviewNotice(msg.text), i)}
                                    className="mt-2 inline-flex items-center gap-1 font-display text-sm text-book-accent/80 hover:text-book-accent border border-book-accent/30 hover:border-book-accent/60 px-3 py-1.5 transition-colors font-medium rounded-full">
                                    {savedIdx === i ? 'Saved to Notes' : saveErrIdx === i ? 'Could not save' : 'Save to Notes'}
                                </button>
                            )}
                        </div>
                    </div>
                ))}
                {typing && (
                    <div className="flex gap-2">
                        <div className="w-7 h-7 rounded-full bg-book-accent/10 border border-book-accent flex items-center justify-center flex-shrink-0">
                            <IconSparkles className="w-3.5 h-3.5 text-book-accent" />
                        </div>
                        <div className="border border-book-accent/20 bg-book-page px-3 py-2 rounded-r-lg rounded-tl-lg" role="status" aria-live="polite">
                            <div className="flex items-center gap-1.5">
                                <div className="w-1.5 h-1.5 rounded-full bg-book-accent animate-pulse"></div>
                                <div className="w-1.5 h-1.5 rounded-full bg-book-accent animate-pulse" style={{ animationDelay: '0.15s' }}></div>
                                <div className="w-1.5 h-1.5 rounded-full bg-book-accent animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                            </div>
                            <span className="block font-serif italic text-book-muted text-xs mt-1.5">{aiWaitCopy('chat', waitLong)}</span>
                        </div>
                    </div>
                )}
                <div ref={endRef}></div>
            </div>

            {/* Composer */}
            <div className="border-t border-book-accent/20 bg-book-bg p-3 flex-shrink-0">
                {mockMode ? (
                    <p className="font-serif italic text-book-muted text-xs text-center py-2">
                        The AI Study Partner needs emulator or firebase mode (no backend in mock mode).
                    </p>
                ) : (
                    <div className="flex gap-2 items-end">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                            }}
                            placeholder="Ask about the text — you can request your preferred language"
                            rows={2}
                            style={{ resize: 'none' }}
                            className="flex-grow bg-book-bg border border-book-accent/30 px-3 py-2 text-book-text font-serif text-sm focus:outline-none focus:border-book-accent" />
                        <button onClick={send} disabled={!input.trim() || typing || historyReady !== conversationKey}
                            className="flex-shrink-0 px-3 py-2 bg-book-accent text-black hover:bg-book-accentLight transition-colors disabled:opacity-30"
                            aria-label="Send">
                            <IconSend className="w-4 h-4" />
                        </button>
                    </div>
                )}
                <AiAllowance packId={packId} /><details className="mt-2 text-xs text-book-muted"><summary className="cursor-pointer font-display">Usage &amp; AI information</summary>
                {entitled ? (
                    <p className="font-serif italic text-book-muted text-xs mt-1.5 text-center font-semibold">
                        Your unlocked pack includes Study Partner access, subject to daily question and token limits.
                    </p>
                ) : (
                    <p className="font-serif italic text-book-accent/80 text-xs mt-1.5 text-center font-semibold">
                        Two free questions per day across all packs. Resets at 00:00 UTC (08:00 Hong Kong / Singapore). Email verification required.
                    </p>
                )}
                <p className="font-serif italic text-book-muted text-xs mt-1 text-center font-semibold">{disclaimer}</p>
                <p className="mt-2">You can ask for an explanation in your preferred language, then practise your answer in English. Accuracy can vary by language; check key points against the text.</p>
                <label className="flex gap-2 mt-3"><input type="checkbox" checked={keepHistory} onChange={e => setHistoryEnabled(e.target.checked).catch(() => setHistoryError('Could not clear local history.'))} />Keep history on this device for 7 days</label>
                <p className="mt-1">Up to 100 messages per pack. Cleared on sign-out. Turn off on shared devices. Only recent messages are used for each reply.</p>
                <button disabled={typing} className="mt-2 underline" onClick={async () => { try { await clearChat(currentUser?.uid, packId); setMessages([greeting]); setSavedIdx(null); } catch { setHistoryError('Could not clear local history.'); } }}>Clear / new conversation</button>
                {historyError && <p role="status">{historyError}</p>}
                </details>
            </div>
            </>)}
        </div>
    );
}
